/**
 * Admin → Usage Dashboard (§27.7 + Phase 29).
 *
 * Route: /admin/usage
 *
 * Three tabs:
 *   Chat     — existing metrics (token consumption, pipeline runs, persona
 *               popularity, active users, chat token telemetry)
 *   Pipeline — per-agent token breakdown + daily pipeline run counts
 *   Combined — stacked chat/pipeline/roundtable tokens per day
 *
 * Each tab fetches its data once and caches it for instant tab switching.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";
import PipelineAgentTable from "../components/admin/PipelineAgentTable.jsx";
import PipelineRecentFailures from "../components/admin/PipelineRecentFailures.jsx";
import PipelineTrendChart from "../components/admin/PipelineTrendChart.jsx";
import {
  buildDefaultSince,
  getAdminUsage,
  getPipelineMetricsAgents,
  getPipelineMetricsFailures,
  getPipelineMetricsTrend,
} from "../services/admin.js";

const TABS = ["chat", "pipeline", "combined", "pipelineMetrics"];

const TAB_LABELS = {
  chat: "Chat",
  pipeline: "Pipeline",
  combined: "Combined",
  pipelineMetrics: "Pipeline Metrics",
};

function formatNumber(n) {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat().format(n);
}

function shortDate(iso) {
  if (!iso || iso.length < 10) return iso;
  return iso.slice(5); // MM-DD
}

function tooltipStyle() {
  return {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "6px",
  };
}

export default function AdminUsage() {
  const [activeTab, setActiveTab] = useState("chat");
  // Cache per-tab so switching is instant after first load
  const [tabData, setTabData] = useState({
    chat: null,
    pipeline: null,
    combined: null,
    pipelineMetrics: null,
  });
  const [loading, setLoading] = useState({
    chat: true,
    pipeline: false,
    combined: false,
    pipelineMetrics: false,
  });
  const [error, setError] = useState({
    chat: null,
    pipeline: null,
    combined: null,
    pipelineMetrics: null,
  });
  const initializedRef = useRef(false);

  const loadTab = useCallback(
    (tab) => {
      if (tabData[tab]) return; // already cached
      // The Pipeline Metrics tab manages its own fetch lifecycle inside
      // <PipelineMetricsTab/> — multiple endpoints + an agent selector
      // can't be modelled by the single-fetch getAdminUsage shape used
      // for the other tabs. Mark it loaded so the dispatcher skips it.
      if (tab === "pipelineMetrics") {
        setTabData((prev) => ({ ...prev, [tab]: { managed: true } }));
        return;
      }
      setLoading((prev) => ({ ...prev, [tab]: true }));
      getAdminUsage(tab)
        .then((resp) => {
          setTabData((prev) => ({ ...prev, [tab]: resp }));
          setLoading((prev) => ({ ...prev, [tab]: false }));
        })
        .catch((err) => {
          setError((prev) => ({ ...prev, [tab]: err.message }));
          setLoading((prev) => ({ ...prev, [tab]: false }));
        });
    },
    [tabData]
  );

  // Load Chat tab on mount
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      if (!tabData.chat) {
        setLoading((prev) => ({ ...prev, chat: true }));
        getAdminUsage("chat")
          .then((resp) => {
            setTabData((prev) => ({ ...prev, chat: resp }));
            setLoading((prev) => ({ ...prev, chat: false }));
          })
          .catch((err) => {
            setError((prev) => ({ ...prev, chat: err.message }));
            setLoading((prev) => ({ ...prev, chat: false }));
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleTabClick(tab) {
    setActiveTab(tab);
    loadTab(tab);
  }

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1>Usage</h1>
      </div>

      <div className="admin-usage__tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`admin-usage__tab${activeTab === tab ? " admin-usage__tab--active" : ""}`}
            onClick={() => handleTabClick(tab)}
          >
            {TAB_LABELS[tab] || tab}
          </button>
        ))}
      </div>

      {activeTab === "chat" && (
        <ChatTab data={tabData.chat} loading={loading.chat} error={error.chat} />
      )}
      {activeTab === "pipeline" && (
        <PipelineTab data={tabData.pipeline} loading={loading.pipeline} error={error.pipeline} />
      )}
      {activeTab === "combined" && (
        <CombinedTab data={tabData.combined} loading={loading.combined} error={error.combined} />
      )}
      {activeTab === "pipelineMetrics" && <PipelineMetricsTab />}
    </div>
  );
}

// ─── Chat tab ────────────────────────────────────────────────────────────────

function ChatTab({ data, loading, error }) {
  if (loading) return <LoadingSkeleton variant="line" />;
  if (error) return <ErrorBubble message={error} />;
  if (!data) return null;

  const { tokenConsumption, pipelineRuns, personaPopularity, activeUsers, telemetry } = data;

  const pipelineData = pipelineRuns.map((row) => ({ ...row, date: shortDate(row.date) }));
  const activeUsersData = activeUsers.map((row) => ({ ...row, date: shortDate(row.date) }));
  const telemetryData = (telemetry || []).map((row) => ({ ...row, date: shortDate(row.date) }));

  return (
    <>
      <section className="admin-usage__stats">
        <StatCard
          label="Tokens used today"
          value={formatNumber(tokenConsumption.today)}
          hint={
            tokenConsumption.totalBudget
              ? `of ${formatNumber(tokenConsumption.totalBudget)} aggregate daily cap`
              : null
          }
        />
        <StatCard
          label="Registered users"
          value={formatNumber(tokenConsumption.userCount)}
          hint={`Metrics window: ${data.windowDays} days`}
        />
        <StatCard
          label="Active personas"
          value={formatNumber(personaPopularity.length)}
          hint={
            personaPopularity.length
              ? `Top: ${personaPopularity[0].name}`
              : "No conversations in window"
          }
        />
      </section>

      <ChartCard title="Chat token telemetry (last 30 days)">
        {telemetryData.length === 0 ? (
          <p className="admin-page__hint">No telemetry data yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={telemetryData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" stroke="var(--text-muted)" fontSize={12} />
              <YAxis stroke="var(--text-muted)" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle()} />
              <Legend />
              <Bar dataKey="inputTokens" stackId="tokens" fill="#818CF8" name="Input" />
              <Bar dataKey="outputTokens" stackId="tokens" fill="#4F46E5" name="Output" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title="Pipeline runs (last 30 days)">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={pipelineData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="date" stroke="var(--text-muted)" fontSize={12} />
            <YAxis stroke="var(--text-muted)" fontSize={12} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle()} />
            <Legend />
            <Bar dataKey="completed" stackId="status" fill="#10B981" />
            <Bar dataKey="failed" stackId="status" fill="#EF4444" />
            <Bar dataKey="cancelled" stackId="status" fill="#9CA3AF" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Persona popularity">
        {personaPopularity.length === 0 ? (
          <p className="admin-page__hint">No conversations recorded yet.</p>
        ) : (
          <ResponsiveContainer
            width="100%"
            height={Math.max(240, personaPopularity.length * 36)}
          >
            <BarChart data={personaPopularity} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                type="number"
                stroke="var(--text-muted)"
                fontSize={12}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                stroke="var(--text-muted)"
                fontSize={12}
                width={140}
              />
              <Tooltip contentStyle={tooltipStyle()} />
              <Bar dataKey="count" fill="#4F46E5">
                {personaPopularity.map((entry) => (
                  <Cell key={entry.personaId} fill="#4F46E5" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title="Active users (last 30 days)">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={activeUsersData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="date" stroke="var(--text-muted)" fontSize={12} />
            <YAxis stroke="var(--text-muted)" fontSize={12} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle()} />
            <Line
              type="monotone"
              dataKey="count"
              stroke="#4F46E5"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </>
  );
}

// ─── Pipeline tab ─────────────────────────────────────────────────────────────

function PipelineTab({ data, loading, error }) {
  if (loading) return <LoadingSkeleton variant="line" />;
  if (error) return <ErrorBubble message={error} />;
  if (!data) return null;

  const { dailySeries = [], tokensPerAgent = [] } = data;

  const dailyData = dailySeries.map((row) => ({ ...row, date: shortDate(row.date) }));

  const totalRuns = dailySeries.reduce((sum, r) => sum + (r.runs || 0), 0);
  const totalTokens = dailySeries.reduce(
    (sum, r) => sum + (r.inputTokens || 0) + (r.outputTokens || 0),
    0
  );

  return (
    <>
      <section className="admin-usage__stats">
        <StatCard label="Pipeline runs (30 days)" value={formatNumber(totalRuns)} />
        <StatCard label="Total tokens (30 days)" value={formatNumber(totalTokens)} />
        <StatCard
          label="Agents instrumented"
          value={formatNumber(tokensPerAgent.length)}
          hint={tokensPerAgent.length ? `Highest: ${tokensPerAgent.reduce((a, b) => (b.totalTokens > a.totalTokens ? b : a)).name || "—"}` : null}
        />
      </section>

      <ChartCard title="Pipeline tokens per day (last 30 days)">
        {dailyData.length === 0 ? (
          <p className="admin-page__hint">No pipeline runs yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" stroke="var(--text-muted)" fontSize={12} />
              <YAxis stroke="var(--text-muted)" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle()} />
              <Legend />
              <Bar dataKey="inputTokens" stackId="tokens" fill="#818CF8" name="Input" />
              <Bar dataKey="outputTokens" stackId="tokens" fill="#4F46E5" name="Output" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title="Pipeline runs per day (last 30 days)">
        {dailyData.length === 0 ? (
          <p className="admin-page__hint">No pipeline runs yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" stroke="var(--text-muted)" fontSize={12} />
              <YAxis stroke="var(--text-muted)" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle()} />
              <Bar dataKey="runs" fill="#10B981" name="Runs" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title="Tokens per agent (last 30 days)">
        {tokensPerAgent.length === 0 ? (
          <p className="admin-page__hint">No agent telemetry yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(240, tokensPerAgent.length * 40)}>
            <BarChart data={tokensPerAgent} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                type="number"
                stroke="var(--text-muted)"
                fontSize={12}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                stroke="var(--text-muted)"
                fontSize={12}
                width={160}
              />
              <Tooltip contentStyle={tooltipStyle()} />
              <Legend />
              <Bar dataKey="inputTokens" stackId="tokens" fill="#818CF8" name="Input" />
              <Bar dataKey="outputTokens" stackId="tokens" fill="#4F46E5" name="Output" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </>
  );
}

// ─── Combined tab ─────────────────────────────────────────────────────────────

function CombinedTab({ data, loading, error }) {
  if (loading) return <LoadingSkeleton variant="line" />;
  if (error) return <ErrorBubble message={error} />;
  if (!data) return null;

  const { combinedDaily = [] } = data;
  const chartData = combinedDaily.map((row) => ({ ...row, date: shortDate(row.date) }));
  const grandTotal = combinedDaily.reduce(
    (sum, r) => sum + (r.chat || 0) + (r.pipeline || 0) + (r.roundtable || 0),
    0
  );

  return (
    <>
      <section className="admin-usage__stats">
        <StatCard label="Total tokens (30 days)" value={formatNumber(grandTotal)} hint="All channels" />
      </section>

      <ChartCard title="Tokens by channel per day (last 30 days)">
        {chartData.length === 0 ? (
          <p className="admin-page__hint">No telemetry data yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" stroke="var(--text-muted)" fontSize={12} />
              <YAxis stroke="var(--text-muted)" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle()} />
              <Legend />
              <Bar dataKey="chat" stackId="ch" fill="#4F46E5" name="Chat" />
              <Bar dataKey="pipeline" stackId="ch" fill="#10B981" name="Pipeline" />
              <Bar dataKey="roundtable" stackId="ch" fill="#818CF8" name="Roundtable" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </>
  );
}

// ─── Pipeline Metrics tab (v5.1 Phase 52) ────────────────────────────────────

function PipelineMetricsTab() {
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentsError, setAgentsError] = useState(null);

  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [trend, setTrend] = useState({ buckets: [] });
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState(null);

  const [failures, setFailures] = useState([]);
  const [failuresLoading, setFailuresLoading] = useState(true);
  const [failuresError, setFailuresError] = useState(null);

  const since = useRef(buildDefaultSince()).current;

  // Initial fetch: agents + failures.
  useEffect(() => {
    setAgentsLoading(true);
    getPipelineMetricsAgents({ since })
      .then((resp) => {
        setAgents(resp.agents || []);
        setAgentsLoading(false);
        // Auto-select the busiest agent so the trend chart isn't empty.
        if (resp.agents && resp.agents.length && !selectedAgentId) {
          const top = [...resp.agents].sort((a, b) => (b.runs || 0) - (a.runs || 0))[0];
          if (top) setSelectedAgentId(top.agentId);
        }
      })
      .catch((err) => {
        setAgentsError(err.message);
        setAgentsLoading(false);
      });

    setFailuresLoading(true);
    getPipelineMetricsFailures({ since, limit: 20 })
      .then((resp) => {
        setFailures(resp.failures || []);
        setFailuresLoading(false);
      })
      .catch((err) => {
        setFailuresError(err.message);
        setFailuresLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trend re-fetches whenever the selected agent changes.
  useEffect(() => {
    if (!selectedAgentId) return;
    setTrendLoading(true);
    setTrendError(null);
    getPipelineMetricsTrend({ since, agentId: selectedAgentId })
      .then((resp) => {
        setTrend(resp || { buckets: [] });
        setTrendLoading(false);
      })
      .catch((err) => {
        setTrendError(err.message);
        setTrendLoading(false);
      });
  }, [selectedAgentId, since]);

  return (
    <>
      <ChartCard title="Per-agent metrics (last 7 days)">
        {agentsLoading ? (
          <LoadingSkeleton variant="line" />
        ) : agentsError ? (
          <ErrorBubble message={agentsError} />
        ) : (
          <PipelineAgentTable
            agents={agents}
            selectedAgentId={selectedAgentId}
            onSelect={setSelectedAgentId}
          />
        )}
      </ChartCard>

      <ChartCard
        title={
          selectedAgentId
            ? `Hourly median duration — ${selectedAgentId}`
            : "Hourly median duration"
        }
      >
        {trendLoading ? (
          <LoadingSkeleton variant="line" />
        ) : trendError ? (
          <ErrorBubble message={trendError} />
        ) : (
          <PipelineTrendChart
            agentId={selectedAgentId}
            buckets={trend.buckets || []}
          />
        )}
      </ChartCard>

      <ChartCard title="Recent failures">
        {failuresLoading ? (
          <LoadingSkeleton variant="line" />
        ) : failuresError ? (
          <ErrorBubble message={failuresError} />
        ) : (
          <PipelineRecentFailures failures={failures} />
        )}
      </ChartCard>
    </>
  );
}

// ─── Shared components ────────────────────────────────────────────────────────

function StatCard({ label, value, hint }) {
  return (
    <div className="admin-usage__stat">
      <div className="admin-usage__stat-label">{label}</div>
      <div className="admin-usage__stat-value">{value}</div>
      {hint && <div className="admin-usage__stat-hint">{hint}</div>}
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <section className="admin-usage__chart">
      <h2 className="admin-usage__chart-title">{title}</h2>
      {children}
    </section>
  );
}
